import { AlertTriangle, UtensilsCrossed, ClipboardList, Clock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

interface OpenConsumptionWarningDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  openTablesCount: number;
  openCommandsCount: number;
  totalOpenAmount: number;
  openTableNumbers?: string[];
  openCommandNumbers?: string[];
  staleTablesCount?: number;
  staleCommandsCount?: number;
  staleAmount?: number;
  staleTableNumbers?: string[];
  staleCommandNumbers?: string[];
}

export function OpenConsumptionWarningDialog({
  open,
  onOpenChange,
  onConfirm,
  openTablesCount,
  openCommandsCount,
  totalOpenAmount,
  openTableNumbers = [],
  openCommandNumbers = [],
  staleTablesCount = 0,
  staleCommandsCount = 0,
  staleAmount = 0,
  staleTableNumbers = [],
  staleCommandNumbers = [],
}: OpenConsumptionWarningDialogProps) {
  const navigate = useNavigate();

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const handleGoToOpenConsumption = () => {
    onOpenChange(false);
    navigate("/dashboard/consumo-aberto");
  };

  const hasStaleConsumptions = staleTablesCount > 0 || staleCommandsCount > 0;
  const totalStale = staleTablesCount + staleCommandsCount;

  const unique = (values: string[]) => Array.from(new Set(values.filter(Boolean)));
  const openTablesUnique = unique(openTableNumbers);
  const openCommandsUnique = unique(openCommandNumbers);
  const staleTablesUnique = unique(staleTableNumbers);
  const staleCommandsUnique = unique(staleCommandNumbers);

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-warning">
            <AlertTriangle className="h-5 w-5" />
            Consumos em Aberto
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-4">
              <p>
                Existem consumos que ainda não foram finalizados. Deseja continuar
                com o fechamento do caixa?
              </p>

              {/* Stale consumptions alert */}
              {hasStaleConsumptions && (
                <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 space-y-2">
                  <div className="flex items-center gap-2 text-destructive font-medium">
                    <Clock className="h-4 w-4" />
                    <span>Atenção: {totalStale} consumo(s) com mais de 24h</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Esses podem ser pedidos órfãos que não foram finalizados corretamente.
                    Recomendamos verificar na página de Consumo Aberto antes de fechar o caixa.
                  </p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {staleTablesCount > 0 && (
                      <Badge variant="destructive" className="text-xs">
                        {staleTablesCount} mesa(s) &gt;24h
                      </Badge>
                    )}
                    {staleCommandsCount > 0 && (
                      <Badge variant="destructive" className="text-xs">
                        {staleCommandsCount} comanda(s) &gt;24h
                      </Badge>
                    )}
                    <Badge variant="outline" className="text-xs border-destructive/50 text-destructive">
                      Total: {formatCurrency(staleAmount)}
                    </Badge>
                  </div>

                  {(staleTablesUnique.length > 0 || staleCommandsUnique.length > 0) && (
                    <div className="pt-2 space-y-1">
                      <p className="text-xs text-muted-foreground">Identificação rápida:</p>
                      <div className="flex flex-wrap gap-2">
                        {staleTablesUnique.map((n) => (
                          <Badge key={`stale-table-${n}`} variant="outline" className="text-xs border-destructive/50 text-destructive">
                            Mesa {n}
                          </Badge>
                        ))}
                        {staleCommandsUnique.map((n) => (
                          <Badge key={`stale-command-${n}`} variant="outline" className="text-xs border-destructive/50 text-destructive">
                            Comanda {n}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="rounded-lg border p-4 space-y-3 bg-muted/50">
                {openTablesCount > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2 text-foreground">
                      <UtensilsCrossed className="h-4 w-4 text-muted-foreground" />
                      Mesas abertas
                    </span>
                    <span className="font-medium text-foreground">{openTablesCount}</span>
                  </div>
                )}

                {openCommandsCount > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2 text-foreground">
                      <ClipboardList className="h-4 w-4 text-muted-foreground" />
                      Comandas abertas
                    </span>
                    <span className="font-medium text-foreground">{openCommandsCount}</span>
                  </div>
                )}

                {(openTablesUnique.length > 0 || openCommandsUnique.length > 0) && (
                  <div className="flex flex-wrap gap-2 pt-1">
                    {openTablesUnique.map((n) => (
                      <Badge key={`open-table-${n}`} variant="secondary" className="text-xs">
                        Mesa {n}
                      </Badge>
                    ))}
                    {openCommandsUnique.map((n) => (
                      <Badge key={`open-command-${n}`} variant="secondary" className="text-xs">
                        Comanda {n}
                      </Badge>
                    ))}
                  </div>
                )}

                <div className="flex items-center justify-between border-t pt-3">
                  <span className="font-medium text-foreground">Total em aberto</span>
                  <span className="font-bold text-lg text-foreground">
                    {formatCurrency(totalOpenAmount)}
                  </span>
                </div>
              </div>

              <p className="text-sm text-muted-foreground">
                Os consumos continuarão abertos e poderão ser finalizados no próximo turno de caixa.
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancelar</AlertDialogCancel>
          <Button variant="outline" onClick={handleGoToOpenConsumption}>
            Ir para Consumo Aberto
          </Button>
          <AlertDialogAction onClick={onConfirm} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
            Continuar Fechamento
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
